import {useMemo} from 'react';

import {RunHistoryRow} from '../../../types/run';
import {RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {
  calculateStepBounds,
  resolveSliderStepCollisions,
  SliderStepMapping,
} from '../utils/stepUtils';

/**
 * Run-level metadata about steps and slider steps
 */
export interface StepMetaRun {
  min: number;
  max: number;
  steps: number[];
  stepSet: Set<number>;
  minSliderStep: number;
  maxSliderStep: number;
  sliderSteps: number[];
  sliderStepSet: Set<number>;
  sliderKey: string;
  sliderStepMap?: Map<number, number>;
}

/**
 * Global metadata about steps and slider steps across all runs
 */
export interface StepMeta {
  runs: StepMetaRun[];
  min: number;
  max: number;
  steps: number[];
  stepSet: Set<number>;
  sliderKey: string;
  minSliderStep: number;
  maxSliderStep: number;
  sliderSteps: number[];
  sliderStepSet: Set<number>;
}

export function useStepMeta(
  runsWithMedia: RunWithHistoryAndMediaWandb[],
  mediaSliderKey: string,
  snapToExistingStep: boolean,
  runStepsMap: Map<string, RunHistoryRow[]>
): StepMeta {
  return useMemo(() => {
    let globalStepMin = Infinity;
    let globalStepMax = -Infinity;
    const globalSteps = new Set<number>();
    const globalSliderSteps = new Set<number>();
    const globalStepsBySliderStep = new Map<number, number>();

    // Build run-level data while recording global stats
    const runs = runsWithMedia.map(runHistory => {
      const steps = runStepsMap.get(runHistory.name);
      if (!steps) {
        // Properly type the empty run case
        return {
          min: 0,
          max: 0,
          steps: [] as number[],
          stepSet: new Set<number>(),
          minSliderStep: 0,
          maxSliderStep: 0,
          sliderSteps: [] as number[],
          sliderStepSet: new Set<number>(),
          sliderKey: mediaSliderKey,
        };
      }

      // Create step to slider step mappings
      const mappings: SliderStepMapping[] = steps.map(
        (history: RunHistoryRow) => ({
          step: history._step,
          sliderStep: history[mediaSliderKey],
        })
      );

      // resolving collisions using the most recent step value
      const {uniqueSliderSteps, stepsBySliderStep, resolvedSteps} =
        resolveSliderStepCollisions(mappings, 'latest');

      const {localResolveMin, localResolvedMax} =
        calculateStepBounds(resolvedSteps);
      const {
        localResolveMin: localSliderResolveMin,
        localResolvedMax: localSliderResolvedMax,
      } = calculateStepBounds(uniqueSliderSteps);

      const runStats: StepMetaRun = {
        min: localResolveMin,
        max: localResolvedMax,
        steps: resolvedSteps,
        stepSet: new Set(resolvedSteps),
        minSliderStep: localSliderResolveMin,
        maxSliderStep: localSliderResolvedMax,
        sliderSteps: uniqueSliderSteps,
        sliderStepSet: new Set(uniqueSliderSteps),
        sliderKey: mediaSliderKey,
        sliderStepMap: stepsBySliderStep,
      };

      // Update global stats
      globalStepMin = Math.min(globalStepMin, runStats.min);
      globalStepMax = Math.max(globalStepMax, runStats.max);
      resolvedSteps.forEach(s => globalSteps.add(s));
      uniqueSliderSteps.forEach(s => globalSliderSteps.add(s));

      // Update global step mapping
      stepsBySliderStep.forEach((step, sliderStep) => {
        if (
          !globalStepsBySliderStep.has(sliderStep) ||
          globalStepsBySliderStep.get(sliderStep)! < step
        ) {
          globalStepsBySliderStep.set(sliderStep, step);
        }
      });

      return runStats;
    });

    // Apply snapToExistingStep logic
    const minStep =
      !snapToExistingStep || globalStepMin === Infinity ? 0 : globalStepMin;
    const maxStep = globalStepMax === -Infinity ? 0 : globalStepMax;

    // Get ordered list of slider steps and their corresponding step values
    const allSliderSteps = Array.from(globalSliderSteps).sort((a, b) => a - b);
    const allSteps = Array.from(globalSteps).sort((a, b) => a - b);

    return {
      runs,
      min: minStep,
      max: maxStep,
      steps: allSteps,
      stepSet: new Set(allSteps),
      sliderKey: mediaSliderKey,
      minSliderStep: allSliderSteps[0] ?? 0,
      maxSliderStep: allSliderSteps[allSliderSteps.length - 1] ?? 0,
      sliderSteps: allSliderSteps,
      sliderStepSet: new Set(allSliderSteps),
    };
  }, [runsWithMedia, mediaSliderKey, snapToExistingStep, runStepsMap]);
}
